import { Fragment, Suspense } from "react";
import { Outlet } from "react-router-dom";
import type { ActionFunction, RouteObject, LoaderFunction } from "react-router-dom";
import { generatePreservedRoutes, generateRegularRoutes } from "@generouted/react-router/core";
import {
  LoadingMojo,
  ReactRouterErrorBoundary,
  loadModuleWithRetry,
  showTranslationErrorNotification,
} from "@web-monorepo/molecules";
import { ModuleError } from "./ModuleError";
import UnmatchedRoute from "./pages/_components/UnmatchedRoute";
import { captureException } from "@web-monorepo/telemetry";
import { loadNamespaceForFile } from "@web-monorepo/i18n";

type Element = () => JSX.Element;
type Module = {
  default: Element;
  Loader?: LoaderFunction;
  Action?: ActionFunction;
  Catch?: Element;
  Pending?: Element;
};

const PRESERVED = import.meta.glob<Module>(["/src/pages/_app.tsx", "/src/pages/404.tsx"], { eager: true });
const ROUTES = import.meta.glob<Module>([
  "/src/pages/**/*.{jsx,tsx}",
  "!**/{_app,404}.*",
  "!**/_components/**/*",
  "!**/_storybook/**",
  "!**/*.stories.*",
  "!**/*.test.tsx",
]);

const preservedRoutes = generatePreservedRoutes<Omit<Module, "Action">>(PRESERVED);

function createRouteFromModule(realizedModule: Partial<Module>) {
  const Element = realizedModule?.default || Fragment;
  const Pending = realizedModule?.Pending;
  const Page = () =>
    Pending ? (
      <Suspense fallback={<Pending />}>
        <Element />
      </Suspense>
    ) : (
      <Element />
    );

  return {
    Component: Page,
    ErrorBoundary: realizedModule?.Catch,
    loader: realizedModule?.Loader,
    action: realizedModule?.Action,
  };
}

const regularRoutes = generateRegularRoutes<RouteObject, () => Promise<Partial<Module>>>(ROUTES, (module, key) => {
  const index = /index\.(jsx|tsx)$/.test(key) && !key.includes("pages/index") ? { index: true } : {};

  return {
    ...index,
    lazy: async () => {
      try {
        // The route's message catalog is fetched alongside its code, and React Router holds the
        // render until both land — which is what lets translation work with no React context and
        // no subscription (see @web-monorepo/i18n). `key` is the module's own path, the same path the
        // extractor used to decide which catalog the strings went into, so nothing declares a
        // namespace by hand. A namespace with no strings resolves rather than throws. A catalog
        // that still fails after its own retries is reported and the route renders anyway: every
        // string carries its English, so that is what shows (with a toast saying so), and the next
        // route load tries again.
        const [realizedModule] = await Promise.all([
          loadModuleWithRetry(module),
          loadNamespaceForFile(key).catch((error: unknown) => {
            captureException(error);
            showTranslationErrorNotification();
          }),
        ]);
        return createRouteFromModule(realizedModule);
        // eslint-disable-next-line no-catch-all/no-catch-all -- we do want to catch all and render an error page.
      } catch (error) {
        captureException(error);

        return {
          Component: () => <ModuleError error={error} />,
        };
      }
    },
  };
});

const _app = preservedRoutes?._app;

const fallback: RouteObject = {
  path: "*",
  element: (
    <Suspense
      fallback={
        <div>
          <LoadingMojo />
        </div>
      }
    >
      <UnmatchedRoute />
    </Suspense>
  ),
};

// This pattern should come into play once we are done 404s falling through to LazyManualRoutes:
// const _404 = preservedRoutes?.["404"];

const Element = _app?.default || Fragment;
const renderApp = () =>
  _app?.Pending ? (
    <Suspense fallback={<_app.Pending />}>
      <Element />
    </Suspense>
  ) : (
    <Element />
  );

const app = { Component: _app?.default ? renderApp : Outlet, ErrorBoundary: _app?.Catch, loader: _app?.Loader };

export const routes: RouteObject[] = [
  {
    ...app,
    errorElement: <ReactRouterErrorBoundary />,
    // With v7_partialHydration enabled (see root.tsx) and all routes lazy, React Router needs a
    // hydrate fallback to render while the initial lazy route resolves; without one it warns
    // "No `HydrateFallback` element provided to render during initial hydration".
    hydrateFallbackElement: <LoadingMojo />,
    children: [...regularRoutes, fallback],
  },
];
